import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, ButtonGroup } from '@pattern-library/buttons';
import TextInput from '@pattern-library/text-input';
import useUnifiedLoginFormValidation from '../../hooks/useUnifiedLoginFormValidation';
import ReCaptcha from '../ReCaptcha/ReCaptcha';

const propTypes = {
    antiForgeryToken: PropTypes.string.isRequired,
    buttonProps: PropTypes.objectOf(PropTypes.string).isRequired,
    formAction: PropTypes.string.isRequired,
    usernameInputProps: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.array]))
        .isRequired,
    code: PropTypes.string,
    captchaErrorId: PropTypes.string.isRequired,
    captchaSiteKey: PropTypes.string.isRequired,
    hasInitialCaptchaError: PropTypes.bool.isRequired,
    captchaDisabled: PropTypes.bool.isRequired,
    disableCaptchaKey: PropTypes.string
};

function ConfirmUsernameForm(props) {
    const {
        antiForgeryToken,
        buttonProps,
        formAction,
        usernameInputProps,
        code,
        captchaErrorId,
        captchaSiteKey,
        hasInitialCaptchaError,
        captchaDisabled,
        disableCaptchaKey
    } = props;

    const [hasCaptchaError, setHasCaptchaError] = useState(hasInitialCaptchaError);
    const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);

    const usernameInputName = usernameInputProps.name;
    const { validation, ...restUsernameInputProps } = usernameInputProps;

    /**
     * @type { import('@pattern-library/base/dist/js/hooks/useFormValidation').FormValidationSchema}
     */
    const schema = { [usernameInputName]: validation };

    const { hasError, onSubmit, errorMessage } = useUnifiedLoginFormValidation({ schema });

    const submitIfCaptchaVerified = useCallback(
        e => {
            if (isCaptchaVerified || captchaDisabled) {
                e.target.submit();
            } else {
                setHasCaptchaError(true);
            }
        },
        [isCaptchaVerified, captchaDisabled]
    );

    const handleSubmit = useCallback(
        e => {
            onSubmit(e, submitIfCaptchaVerified);
        },
        [onSubmit, submitIfCaptchaVerified]
    );

    const onVerifyCaptcha = useCallback(() => {
        setIsCaptchaVerified(true);
        setHasCaptchaError(false);
    }, []);

    const onExpiredCaptcha = useCallback(() => {
        setIsCaptchaVerified(false);
    }, []);

    useEffect(() => {
        const captchaErrorEl = document.getElementById(captchaErrorId);
        if (captchaErrorEl) {
            captchaErrorEl.classList.toggle('asc-hidden', !hasCaptchaError);
        }
    }, [captchaErrorId, hasCaptchaError]);

    const buttonDescribedBy =
        [buttonProps.customAttrs?.['aria-describedby'], hasCaptchaError ? captchaErrorId : null]
            .filter(Boolean)
            .join(' ') || undefined;

    return (
        <div className="ul-login-form">
            <form
                action={formAction}
                method="post"
                onSubmit={handleSubmit}
                autoComplete="false"
                noValidate
            >
                <div dangerouslySetInnerHTML={{ __html: antiForgeryToken }} />
                {code && <input type="hidden" id="code" name="code" value={code} />}
                {disableCaptchaKey && (
                    <input
                        type="hidden"
                        id="DisableCaptchaKey"
                        name="DisableCaptchaKey"
                        value={disableCaptchaKey}
                    />
                )}

                <TextInput
                    isRequired
                    autoComplete="off"
                    maxLength={255}
                    hasError={hasError(usernameInputName)}
                    errorMessage={errorMessage(usernameInputName)}
                    {...restUsernameInputProps}
                />

                {/* Submission is allowed without verification when the CAPTCHA is
                    disabled, so there is nothing to show and no reason to load it. */}
                {!captchaDisabled && (
                    <ReCaptcha
                        id="forgot-password-captcha"
                        verifyCallback={onVerifyCaptcha}
                        expiredCallback={onExpiredCaptcha}
                        siteKey={captchaSiteKey}
                    />
                )}

                <ButtonGroup alignment="center" className="asc-mt-4 asc-mb-0">
                    <Button
                        type="submit"
                        className="asc-mtb-0"
                        {...buttonProps}
                        customAttrs={{
                            ...buttonProps.customAttrs,
                            'aria-describedby': buttonDescribedBy
                        }}
                    />
                </ButtonGroup>
            </form>
        </div>
    );
}

ConfirmUsernameForm.propTypes = propTypes;
export default ConfirmUsernameForm;
